import React, { useState, useEffect, Fragment, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import CheckoutPlaceOrderContent from "./CheckoutPlaceOrderContent"
import {
  cdsBroadcastChannel,
  createOfflineOrder,
  printOrder,
  validateCartContents
} from "utils"
import {
  FoodSidebarEditOrderModal,
  CartStockValidationResultModal
} from "components"
import { showWarningTopup } from "@redux"
import { getOrders, sendOrder } from "services"
import classes from "./CheckoutPlaceOrder.module.scss"

const CheckoutPlaceOrder = (props) => {
  const {
    note,
    onComplete,
    isOnline,
    discounts,
    orderItems,
    orderCombos,
    deliveryFees,
    deliveryServiceOutOfRange,
    snapboxQuotaionLoading,
    selectedUserAccount,
    printCustomerReceipt
  } = props

  const [t] = useTranslation()

  const history = useHistory()

  const dispatch = useDispatch()

  const order =
    useSelector(state => state.order)

  const shiftCashier =
    useSelector(state => state.shiftCashier)

  const {
    areaName,
    deliveryType,
    deliveryAddress,
    deliveryService
  } = useSelector(state => state.deliveryInfo)

  const userAccountInfo =
    useSelector(state => state.userAccountInfo)

  const temporaryOff =
    useSelector(state =>
      state.systemSettings.temporaryOff
    )

  const [sending, setSending] = useState(false)
  const [openEditOrderModal, setOpenEditOrderModal] = useState(false)
  const [problematicItems, setProblematicItems] = useState([])
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)

  const singleTableOrderDetails = useRef(null)

  const unmount = useRef(null)

  const sendEventsToCds = (data) => {
    const bc = cdsBroadcastChannel.get()

    if (!bc) return

    bc.postMessage({
      type: "success",
      ...data
    })
  }

  const handleNewOrderCreated = () => {
    history.push("/foods")

    dispatch(
      showWarningTopup(t("سفارش_با_موفقیت_انجام_شد"))
    )
  }

  const handleOrderCombined = () => {
    history.push("/foods")

    dispatch(
      showWarningTopup(t("سفارش ترکیب شد"))
    )
  }

  const getAvailableCoupon = () => {
    const result = []

    discounts.forEach(discount =>
      result.push(discount.couponCode)
    )

    orderCombos.forEach(combo =>
      result.push(combo.code)
    )

    return result
  }

  const validateCart = async () => {
    const previousOrder = null

    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos,
        previousOrder
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(problematicItems)

    setOpenCartValidationResultModal(true)

    setSending(false)

    return false
  }

  const getRequestBody = () => {
    const oItems =
      orderItems.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si => ({
            _id: si._id,
            quantity: si.quantity,
            builderStepKey: si.builderStepKey,
            portion: si.portion
          }))
      }))

    const cItems = []
    orderCombos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        cItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            groupNumber: cIndex + 1,
            comboSetItemId: comboItem.comboSetItemId
          }
        })
      )
    )

    const items = [...cItems, ...oItems]

    const couponCodes = getAvailableCoupon()

    const data = {
      note,
      items,
      isCod: true,
      isPrinted: true,
      deliveryType,
      branchId: userAccountInfo.branchId,
      couponCodes,
      deliveryAddress,
      areaName,
      paymentType: "cod",
      deliveryService:
        deliveryType !== "delivery" ? "internal" : deliveryService
    }

    const shiftId =
      shiftCashier[userAccountInfo._id]._id

    if (shiftId) {
      data["shiftId"] = shiftId
    }

    if (deliveryType === "delivery") {
      data["address"] = deliveryAddress

      if (deliveryService === "snapbox") {
        data["riderQuotationId"] = deliveryFees.quotationId
      }
    }

    if (deliveryType === "dineIn") {
      data["tableNumber"] = order.tableNumber
    }

    return data
  }

  const validate = () => {


    return true
  }

  const doesTableHaveSingleOrder = async () => {
    if (singleTableOrderDetails.current !== null) return

    try {
      const response =
        await getOrders({
          skip: 0,
          limit: 2,
          sortBy: "newstOrder",
          statusMultiple: ["pending"],
          tableNumber: order.tableNumber
        })

      const result =
        response.length === 1 &&
        selectedUserAccount?._id === response[0]?.accountId

      singleTableOrderDetails.current =
        result ? response[0] : null

      setOpenEditOrderModal(result)

      return result
    } catch (err) {
      console.log(err)
    }

    return false
  }

  const handlePlaceOrder = async () => {
    if (!validate()) return

    setSending(true)

    try {
      const tableHasSingleOrder =
        await doesTableHaveSingleOrder()

      if (tableHasSingleOrder) {
        return setSending(false)
      }

      const result = await validateCart()

      if (!result) return

      const requestBody = getRequestBody()

      const accountId =
        selectedUserAccount?._id || ""

      const { order } =
        await sendOrder(requestBody, { accountId })

      sendEventsToCds({ message: t("سفارش_با_موفقیت_ثبت_شد") })

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("سفارش_با_موفقیت_انجام_شد"))
      )

      printCustomerReceipt && printOrder(order)

      if (unmount.current) return

      setSending(false)
    } catch (err) {
      console.log(err)

      dispatch(
        showWarningTopup(t("سفارش_انجام_نشد"))
      )

      if (unmount.current) return

      setSending(false)
    }
  }

  const handlePressOffline = () => {
    if (!validate()) return

    const offlineOrderDetails = {
      note,
      order,
      branchId: userAccountInfo.branchId,
      areaName,
      deliveryType,
      orderStatus: "pending",
      offlineOrderId: ""
    }

    createOfflineOrder(
      offlineOrderDetails,
      printCustomerReceipt
    )

    sendEventsToCds({ message: t("سفارش_با_موفقیت_ثبت_شد") })

    onComplete()

    dispatch(
      showWarningTopup(t("سفارش_انجام_شد"))
    )

    history.push("/foods")
  }

  useEffect(() => {
    unmount.current = false

    return () => { unmount.current = true }
  }, [])

  useEffect(() => {
    singleTableOrderDetails.current = null
  }, [order.tableNumber])

  const internalDeliveryDisabled =
    deliveryService === "internal" &&
    deliveryAddress.areaName === "Other Area"

  const disabledButton =
    sending ||
    temporaryOff ||
    snapboxQuotaionLoading ||
    deliveryServiceOutOfRange ||
    internalDeliveryDisabled

  return (
    <Fragment>
      <div className={classes["container"]}>
        <button
          disabled={disabledButton}
          onClick={isOnline ? handlePlaceOrder : handlePressOffline}
          className={clsx(
            classes["button"],
            disabledButton && classes["disabled"]
          )}
        >
          <CheckoutPlaceOrderContent
            sending={sending}
            internalDeliveryDisabled={internalDeliveryDisabled}
            deliveryServiceOutOfRange={deliveryServiceOutOfRange}
            snapboxQuotaionLoading={snapboxQuotaionLoading}
          />
        </button>
      </div>

      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />

      <FoodSidebarEditOrderModal
        onExited={() => singleTableOrderDetails.current = null}
        onNewOrderCreated={handleNewOrderCreated}
        onOrderCombined={handleOrderCombined}
        tableNumber={order.tableNumber}
        singleTableOrderDetails={singleTableOrderDetails}
        openEditOrderModal={openEditOrderModal}
        setOpenEditOrderModal={setOpenEditOrderModal}
      />
    </Fragment>
  )
}

export default React.memo(CheckoutPlaceOrder)
